import { useTranslate } from '@growtherapy/i18n';
import { Divider, DrawerBody, DrawerFooter, DrawerTitle, Skeleton, cn } from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks/useTheme';
import { Theme } from '../../state/theme';
import { DrawerHeader } from '../DrawerHeader';
import { PageComponentProps, PaginatedDrawer, PaginatedDrawerProps } from '../PaginatedDrawer';
import { type MeasureSchemaAndAnswers } from './types';

/** Centers the drawer title in the same 40px box as the close button. */
const TITLE_ROW_CLASS_NAME = 'flex items-center arc:min-h-component-125';

export function MeasuresDrawer(
  props: Omit<PaginatedDrawerProps<MeasureSchemaAndAnswers>, 'headerComponent' | 'contentOnly'>,
) {
  return <PaginatedDrawer {...props} headerComponent={Header} contentOnly />;
}

function MeasureQuestionSkeleton() {
  return (
    <>
      <Skeleton className='w-2/3 self-start mx-0' />
      <Skeleton className='w-36 self-start mx-0 my-1' />
      <Skeleton className='w-32 self-start mx-0 my-1' />
      <Skeleton className='w-24 self-start mx-0 my-1' />
      <Skeleton className='w-36 self-start mx-0 mt-1 mb-6' />
    </>
  );
}

export function MeasuresDrawerLoadingSkeleton() {
  return (
    <>
      <DrawerHeader>
        <div className={TITLE_ROW_CLASS_NAME} />
      </DrawerHeader>
      <DrawerBody className='overflow-hidden'>
        <span className='sr-only'>Loading outstanding measures...</span>
        <div className='flex flex-col items-center' aria-hidden>
          <Skeleton className='h-16 my-4' />
          <Skeleton className='w-full mb-0' />
          <Skeleton />
          <Skeleton className='w-full mt-12' />
          <Divider orientation='horizontal' className='my-5' />
          <MeasureQuestionSkeleton />
          <MeasureQuestionSkeleton />
          <MeasureQuestionSkeleton />
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className='flex w-full gap-4 justify-end'>
          <Skeleton className='w-24 h-14 rounded-full m-0' />
        </div>
      </DrawerFooter>
    </>
  );
}

function Header({ pageCount, pageIndex }: PageComponentProps<MeasureSchemaAndAnswers>) {
  const t = useTranslate();
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  return (
    <DrawerTitle>
      <div
        className={cn(TITLE_ROW_CLASS_NAME, 'rebrand:typography-subhead-small', {
          'rebrand:text-text-inverse': isDarkTheme,
        })}
      >
        {t('telehealth-core:waitingRoom.questionnaireAofB', { index: pageIndex + 1, total: pageCount })}
      </div>
    </DrawerTitle>
  );
}
